import type { OrbState } from 'thinking-orbs';

import { CHAT_CHANNEL_TYPE, ChatMessage } from '@/types/chat';

/** Light accent at 65% — shared by the header status line and thinking timeline. */
export const AGENT_THINKING_CHROME_COLOR = 'rgba(211, 190, 244, 0.65)';

export type AgentThinkingHint = {
  status?: string;
  toolName?: string;
};

export type OnAgentThinking = (
  isThinking: boolean,
  hint?: AgentThinkingHint
) => void;

export function resolveAgentThinkingState(
  status?: string | null,
  toolName?: string | null
): OrbState {
  const haystack = `${status ?? ''} ${toolName ?? ''}`.toLowerCase();

  if (/transcrib|listen|voice|audio|dictat/.test(haystack)) {
    return 'listening';
  }
  if (/prepar|read|search|context|document|retriev|knowledge/.test(haystack)) {
    return 'searching';
  }
  if (/connect/.test(haystack)) {
    return 'connecting';
  }
  if (/compos|draft|writ/.test(haystack)) {
    return 'composing';
  }
  if (/solv|match|score|calcul|patch/.test(haystack)) {
    return 'solving';
  }
  if (/think/.test(haystack)) {
    return 'breathing';
  }
  return 'working';
}

const THINKING_PHASE = {
  thinking: 'Thinking…',
  working: 'Working…',
  writing: 'Writing…',
} as const;

export function thinkingHintFromMessages(
  messages: ChatMessage[]
): AgentThinkingHint {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.type !== CHAT_CHANNEL_TYPE.BOT) continue;
    if (!(message.isLoader || message.isStreaming || message.isLiveStream)) {
      continue;
    }

    // The header shows the phase only. Step notes and tool names live in the
    // working panel under the message; repeating them here is noise.
    const steps = message.thinkingSteps ?? [];
    const running = [...steps].reverse().find(step => step.state === 'running');

    if (running?.kind === 'tool' || message.toolActivity) {
      return {
        status: THINKING_PHASE.working,
        toolName:
          running?.kind === 'tool'
            ? running.toolName
            : message.toolActivity?.toolName,
      };
    }
    // Fixed labels such as "Preparing context…" or "Transcribing…".
    if (running?.kind === 'status') {
      return { status: running.label };
    }
    if (message.content?.trim()) {
      return { status: THINKING_PHASE.writing };
    }
    if (message.progressStatus) {
      return { status: THINKING_PHASE.thinking };
    }

    return { status: message.streamStatus ?? THINKING_PHASE.thinking };
  }

  return {};
}
